// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { constants, DivWrapper } from "@flip/XDL/constants";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, layoutContainer, xScreen } from "@flip/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridOffset } from "@insite/mobius/GridContainer";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import styled, { css } from "styled-components";

export const FooterContainerWrapper = styled.div`
    border-top: 4px solid ${color("primary")};
    padding: 22px 16px 16px;
    ${xScreen("lte", "lg")} {
        padding-top: 24px;
    }
    ${xScreen("lte", "xs")} {
        padding-bottom: 36px;
    }

    ${({ isCheckoutPage }: any) => {
        if (isCheckoutPage) {
            return css`
                padding: 16px;
                border-top: none;
                background: ${color("common.ui5")};

                ${xScreen("lte", "xs")} {
                    padding-bottom: 16px;
                }
            `;
        }
    }}
`;

export const FooterContentWrapper = styled.div`
    flex-grow: 1;
    width: 100%;
    margin: 0 auto;
    display: flex;
    column-gap: 22px;
    ${xScreen("lte", "xs")} {
        flex-direction: column;
    }
`;

export const FooterUpperContentWrapper = styled.div`
    flex-grow: 1;
    width: 100%;
    display: flex;
    margin: 0 auto;
    column-gap: 22px;
    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    ${xScreen("lte", "md")} {
        flex-direction: column;
    }

    ${xScreen("gte", "lg")} {
        ${layoutContainer}
    }
`;

export const FooterSiteMapLinksWrapper = styled.div`
    .wrapper {
        display: flex;
        align-items: center;
    }
`;

export const GeneralLinksWrapper = styled.div`
    width: 100%;
    ${xScreen("lte", "sm")} {
        margin-left: 0;
    }
    .list-wrapper {
        padding: 0;
        .title-wrapper {
            .list-title {
                > span {
                    text-transform: capitalize;
                    font-size: 16px;
                    font-weight: bold;
                    letter-spacing: 0;
                    line-height: 22px;
                    color: ${getColor("common.ui6")};
                }
                &:hover {
                    > span {
                        text-decoration: underline;
                    }
                }
            }
        }
        .links-wrapper {
            margin-bottom: 0;
            ${xScreen("lte", "md")} {
                padding-right: 0;
            }
            .list-link {
                > span {
                    color: ${getColor("common.ui6")};
                    font-size: 12px;
                    letter-spacing: 0;
                    line-height: 13px;
                    text-transform: capitalize;
                }
                &:hover {
                    > span {
                        text-decoration: underline;
                    }
                }
            }
        }
    }
`;

export const RightSectionWrapper = styled.div`
    max-width: 656px;
    width: 100%;

    ${xScreen("lt", "lg")} {
        margin-bottom: 42px;
        max-width: 100%;

        .email-subsctiption-wrapper {
            & > div {
                margin-bottom: 30px;
            }
        }
    }

    ${xScreen("lt", "md")} {
        margin-bottom: 0;
    }
`;

export const LeftSectionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;

    && > .logo-wrapper .wrapper {
        max-width: 150px;
        max-height: 47px;
        width: 150px;
    }

    && > .address-wrapper {
        margin-top: 24px;
        display: flex;
        column-gap: 22px;

        span,
        a {
            font-size: 12px;
            letter-spacing: 0;
            line-height: 13px;
            font-weight: ${fontConfig.weight.regular};
        }
        a > span {
            font-weight: ${fontConfig.weight.semiBold};
            margin-top: 8px;
        }

        a:first-of-type > span {
            margin-top: 0;
        }

        span {
            display: block;
        }

        & > .phone-email-wrapper {
            width: 100%;
            letter-spacing: 0;
            a > span {
                font-size: 14px;
                font-weight: ${fontConfig.weight.semiBold};
                line-height: 22px;
            }
            span {
                font-size: 12px;
                line-height: 13px;
                margin-top: 7px;
            }
            small {
                font-size: 10px;
                line-height: 11px;
                margin-top: 5px;
                display: block;
            }
        }
    }

    && > .footer-icons-subscription-wrapper {
        column-gap: 22px;
        display: flex;
        .email-subsctiption-wrapper {
            margin-top: 40px;
        }

        .footer-icons-wrapper {
            ${xScreen("gte", constants.footer.breakpoints.xl)} {
                padding-right: 50px;
            }
            width: 100%;
            margin-top: 40px;
        }

        .social-links-wrapper,
        .email-subsctiption-wrapper {
            width: 100%;
            ${xScreen("lte", "lg")} {
                & > div {
                    margin-bottom: 30px;
                }
            }
        }
    }

    .footer-icons-wrapper {
        .awards-logos-wrapper ${DivWrapper} {
            display: flex;
            flex-direction: column;

            .img-wrapper {
                text-align: left;
            }

            & ${GridOffset} {
                flex-wrap: nowrap;
            }
        }
    }

    .links-logos-wrapper {
        max-width: 204px;
        width: 100%;
        ${xScreen("lte", "md")} {
            max-width: 100%;
            display: flex;
            column-gap: 22px;
            margin: 0;

            .social-links {
                max-width: 160px;
            }
            & > div {
                flex: 1;
            }
        }

        ${xScreen("lte", "xs")} {
            margin-top: 30px;
        }
    }
`;

export const FooterBottomSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 100%;
    margin: 32px auto 0 auto;

    ${xScreen("lt", "sm")} {
        align-items: flex-start;
        flex-direction: column;
        row-gap: 16px;
    }

    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map(mw =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    ${({ isCheckoutPage }: any) => {
        if (isCheckoutPage) {
            return css`
                margin: 0 auto;
                ${xScreen("lt", "sm")} {
                    row-gap: 8px;
                }
            `;
        }
    }}
`;

export const CopyRightWrapper = styled.div`
    font-size: 12px;
    letter-spacing: 0;
    line-height: 13px;
    color: ${getColor("common.ui6")};

    ${({ isCheckoutPage }: any) => {
        if (isCheckoutPage) {
            return css`
                color: ${color("common.ui1")};
            `;
        }
    }}
`;
